import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import Box from "@mui/material/Box/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ROWS_PER_PAGE } from "../App";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import BadgeCreateDialog from "../components/BadgeSection/BadgeCreateDialog";
import BadgeEditDialog from "../components/BadgeSection/BadgeEditDialog";
import BadgeViewDialog from "../components/BadgeSection/BadgeViewDialog";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { useBadgeList } from "../hooks/useBadgeList";
import useUpdateUrl from "../hooks/useUpdateUrl";
import Badge from "../models/Badge";
import { Token } from "../theme";
import { Routes } from "../types/Routes";

export default function BadgeList() {
  const { t } = useTranslation();
  const [name, setName] = useState<string>("");
  const { onDataFetch, forceUpdateOn, isLoading, openCreateModal, selectedFile, badgeActions, badgeItems } = useBadgeList(name);

  const searchParams = new URLSearchParams(useLocation().search);
  const titleFilter = searchParams.get("title");
  const { updateUrl } = useUpdateUrl(Routes.BADGES, searchParams);

  useEffect(() => {
    if (titleFilter !== null && titleFilter !== "") {
      setName(titleFilter);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AppToolbar title={t("Badges")}>
        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", padding: "5px" }}>
          <TextField
            label="Badge title"
            name="name"
            style={{ maxWidth: "250px" }}
            variant="outlined"
            value={name}
            size="small"
            onChange={(e: any) => {
              setName(e.target.value);
              searchParams.set("title", e.target.value);
              updateUrl();
            }}
          />
        </Box>
      </AppToolbar>

      <TableBasedCrud<Badge>
        elementName={"Badge"}
        disableAutoFetch
        delayFetch={name}
        rowsPerPage={ROWS_PER_PAGE}
        denseTable
        forceUpdateOn={forceUpdateOn}
        setIsEditModeActive={badgeActions.setEditMode}
        onDataFetch={onDataFetch}
        validateBeforeClosing
        additionalToolbarButtons={() => (
          <Button
            startIcon={<AddIcon />}
            sx={{
              color: Token.textOnAction,
              backgroundColor: Token.actionPrimary,
              "&:hover": { backgroundColor: Token.actionPrimaryHover },
            }}
            onClick={() => {
              badgeActions.setOpenCreateModal(true);
            }}
          >
            Add New Badge
          </Button>
        )}
        onViewMode={(instance) => badgeActions.handleOpenViewDialog(instance)}
        renderDialogContent={() => <></>}
        resetValue={undefined}
        tableColumns={[
          {
            header: t("Icon"),
            content: (instance) => (
              <>
                <OpsAvatarComponent
                  avatarUrl={instance.iconUrl}
                  forceGenericAvatar={true}
                  genericAvatar={<NoPhotographyIcon />}
                  sx={{ width: "2.5em", height: "2.5em" }}
                />
              </>
            ),
          },
          {
            header: t("Title"),
            content: (instance) => <>{instance.name}</>,
          },
          {
            header: t("Description"),
            content: (instance) => (
              <Box sx={{ maxWidth: "30rem", flexWrap: "wrap", overflowWrap: "break-word" }}>{instance.description}</Box>
            ),
          },
          {
            header: t("Automatic approval"),
            content: (instance) => (
              <>{instance.automaticApproval === true ? <CheckBoxIcon color="success" /> : <CancelIcon color="warning" />}</>
            ),
          },
          {
            header: t("Level Option"),
            content: (instance) => <>{instance.level === true ? <CheckBoxIcon color="success" /> : <CancelIcon color="warning" />}</>,
          },
          {
            header: t("Status"),
            content: (instance) => (
              <Button
                variant="outlined"
                disabled
                style={{
                  textTransform: "none",
                  color: "white",
                  backgroundColor: instance.enabled ? "green" : "orange",
                  maxWidth: "150px",
                }}
                fullWidth
              >
                {instance.enabled ? "Enabled" : "Disabled"}
              </Button>
            ),
          },
        ]}
        buildAdditionalActions={(badge) => {
          return [
            {
              title: t(badge.enabled ? "Disable" : "Enable"),
              action: () => badgeActions.setSelectedBadge(badge),
            },
            {
              title: "Edit",
              action: () => badgeActions.handleOpenEditDialog(badge),
            },
            {
              title: t("Delete"),
              action: () => badgeActions.setBadgeToDelete(badge),
            },
          ];
        }}
      />

      <BadgeCreateDialog
        onClose={badgeActions.closeCreateDialog}
        isLoading={isLoading}
        open={openCreateModal}
        handleUploadImage={badgeActions.handleUploadImage}
        selectedFile={selectedFile ? selectedFile : ""}
        handleOnCreateBadge={badgeActions.handleOnCreateBadge}
        handleImageDelete={badgeActions.handleImageDelete}
      />

      {badgeItems.viewBadge && (
        <BadgeViewDialog viewBadge={badgeItems.viewBadge} onClose={() => badgeActions.setViewBadge(undefined)} isLoading={false} />
      )}

      <BadgeEditDialog
        onClose={badgeActions.closeEditDialog}
        handleOnUpdate={badgeActions.handleOnUpdate}
        open={!!badgeItems.editMode}
        isLoading={isLoading}
        handleImageDelete={badgeActions.handleImageDelete}
        handleUploadImage={badgeActions.handleUploadImage}
        selectedFile={selectedFile!}
        editBadge={badgeItems.editBadge!}
      />

      <AppDialog
        open={!!badgeItems.selectedBadge && !badgeItems.editMode}
        onClose={() => badgeActions.setSelectedBadge(undefined)}
        title={badgeItems.selectedBadge?.enabled ? "Disable" : "Enable"}
        content={
          <>
            <Typography>{`The next user is going to be ${badgeItems.selectedBadge?.enabled ? "disabled" : "enabled"}`}</Typography>
            <Typography>Do you want to confirm this action?</Typography>
          </>
        }
        buttons={[
          {
            label: `Confirm ${badgeItems.selectedBadge?.enabled ? "Disable" : "Enable"}`,
            onClick: () => badgeActions.handleBadgeStatusChange(),
          },
        ]}
      />

      <AppDialog
        open={!!badgeItems.badgeToDelete}
        onClose={() => badgeActions.setBadgeToDelete(undefined)}
        title={t("Delete")}
        content={
          <>
            <Typography>{`The next badge is going to be eliminated: ${badgeItems.badgeToDelete?.name}.`}</Typography>
            <Typography>Do you want to confirm this action?</Typography>
          </>
        }
        buttons={[{ label: t("Confirm delete"), onClick: () => badgeActions.handleOnDeleteBadge() }]}
      />
    </>
  );
}
